import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import api from 'connections/api';
import routes_api from 'connections/routes_api';
import { fetchFinancialFlows } from 'views/finance/moduleServices/flow_requests';

// Utility functions
const roundDecimal = (value, decimals) => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

const normalizeDate = (value) => {
  if (!value) return '';
  let dateObj;
  if (typeof value === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    dateObj = new Date(excelEpoch.getTime() + value * 86400000);
  } else {
    dateObj = new Date(value);
  }
  return !isNaN(dateObj.getTime()) ? dateObj.toISOString().split('T')[0] : '';
};

const normalizeHour = (value) => {
  if (!value && value !== 0) return '00:00:00';
  if (typeof value === 'number') {
    const totalSeconds = Math.round(value * 24 * 60 * 60);
    const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }
  return value;
};

// Fetch all paginated flows
const fetchAllFlows = async () => {
  const BATCH_SIZE = 10000
  let allFlows = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const batch = await fetchFinancialFlows({ limit: BATCH_SIZE, offset });
    allFlows = [...allFlows, ...batch];
    offset += batch.length;
    hasMore = batch.length > 0 && batch.length === BATCH_SIZE;  // continue while full batch
  }

  return allFlows;
};

const GeneralCardMassiveUpdate = ({ reload }) => {
  const fileInputRef = useRef(null);
  const [importMode, setImportMode] = useState('append');
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const resetState = () => {
    setSelectedFile(null);
    setParsedData([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const processFile = (file) => {
    if (!file) return;
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: 'binary' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(worksheet);

        const normalized = data.map((item) => ({
          ...item,
          date: normalizeDate(item.date),
          hour: normalizeHour(item.hour),
          exchange_rate: item.exchange_rate ? roundDecimal(Number(item.exchange_rate), 6) : '',
          flow_value: item.flow_value !== undefined ? roundDecimal(Number(item.flow_value), 2) : 0,
          std_value: item.std_value !== undefined ? roundDecimal(Number(item.std_value), 2) : 0,
        }));

        setParsedData(normalized);
        toast.success('File loaded successfully');
      } catch (err) {
        console.error(err);
        toast.error('Failed to read file');
        resetState();
      }
    };

    reader.readAsBinaryString(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      processFile(file);
    } else if (file) {
      toast.error('Please use an Excel file (.xlsx or .xls).');
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const flows_data = await fetchAllFlows();

      if (!flows_data.length) {
        toast.error('No records to export');
        setIsExporting(false);
        return;
      }

      // Ao exportar:
      const exportHeaders = [
        'id','date','hour','desc','categ_l0','categ_l1','categ_l2','reference','flow_type','flow_value','flow_currency','flow_account','exchange_id','exchange_rate','std_value','created_at',
        'extras_id','extras_description','extras_category','extras_cardnumber','extras_info'
      ];

      const dataRows = flows_data.map(item => [
        item.id,
        item.date,
        item.hour,
        item.desc,
        item.categ_l0,
        item.categ_l1,
        item.categ_l2,
        item.reference,
        item.flow_type,
        Number(item.flow_value),
        item.flow_currency,
        item.flow_account,
        item.exchange_id,
        Number(item.exchange_rate),
        Number(item.std_value),
        item.created_at,
        item.extras_id,
        item.extras_description,
        item.extras_category,
        item.extras_cardnumber,
        item.extras_info
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([exportHeaders, ...dataRows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'FinancialFlows');
      XLSX.writeFile(workbook, 'financial_flows.xlsx');

      toast.success('Export completed');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export records');
    } finally {
      setIsExporting(false);
    }
  };

  const deleteAllFinancialFlows = async () => {
    await api.delete(routes_api.finance_flows_deleteall);
  };

  const createManyFinancialFlows = async (payload) => {
    await api.post(routes_api.finance_flows_createmany, payload);
  };

  const proceedImport = async () => {
    setShowConfirmModal(false);
    setIsImporting(true);
    try {
      await deleteAllFinancialFlows();
      await createManyFinancialFlows(parsedData);
      toast.success('Imported successfully (override)');
      resetState();
      if (reload) reload();
    } catch (err) {
      console.error(err);
      toast.error('Error importing records');
    } finally {
      setIsImporting(false);
    }
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast.error('No data to import');
      return;
    }

    if (importMode === 'override') {
      setShowConfirmModal(true);
      return;
    }

    try {
      setIsImporting(true);
      await createManyFinancialFlows(parsedData);
      toast.success('Imported successfully (append)');
      resetState();
      if (reload) reload();
    } catch (err) {
      console.error(err);
      toast.error('Error importing records');
    } finally {
      setIsImporting(false);
    }
  };

  const buttonBase = 'px-6 py-3 rounded-xl shadow-md text-white';
  const importDisabled = 'bg-gray-400 cursor-not-allowed';
  const importEnabled = 'bg-emerald-600 hover:bg-emerald-700';
  const exportDisabled = 'bg-gray-400 cursor-not-allowed';
  const exportEnabled = 'bg-blue-600 hover:bg-blue-700';

  return (
    <>
      <div>
        <div className="flex flex-col sm:flex-row gap-4 items-stretch w-full">
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`flex-1 min-h-[120px] flex items-center justify-center rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
              isDragging
                ? 'border-green-600 bg-green-50'
                : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-green-500'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            <span className="text-sm font-medium text-gray-600">
              {isDragging ? 'Drop file here' : 'Select or drag file'}
            </span>
          </div>

          <div className="flex flex-col justify-center gap-2 sm:min-w-[180px]">
            <div className="text-sm text-gray-700">
              Uploaded file: <strong>{selectedFile?.name || 'none'}</strong>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="flow-import-mode" className="text-xs font-semibold text-gray-600">
                Import mode
              </label>
              <select
                id="flow-import-mode"
                value={importMode}
                onChange={(e) => setImportMode(e.target.value)}
                className="w-full border border-gray-300 px-2 py-2 rounded-xl text-sm text-gray-800 bg-white"
              >
                <option value="append">Append</option>
                <option value="override">Override</option>
              </select>
            </div>
            <button
              type="button"
              onClick={handleImport}
              disabled={!selectedFile || isImporting}
              className={`${buttonBase} ${!selectedFile || isImporting ? importDisabled : importEnabled}`}
            >
              {isImporting ? 'Importing...' : 'Import'}
            </button>
          </div>

          <div className="flex flex-col gap-2 sm:min-w-[140px]">
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className={`${buttonBase} ${isExporting ? exportDisabled : exportEnabled}`}
              title="Export all flows to Excel"
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm override</h3>
            <p className="mb-6 text-sm text-gray-700">
              This will delete <strong>all existing records</strong> before importing the new ones. Are you sure?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={proceedImport}
                className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white shadow"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GeneralCardMassiveUpdate;
